<script setup>
  import { computed } from 'vue'
  import { useTransformLessonCard } from '../../composables/useTransformLessonCard'
  import continueCardIcon from '@/assets/images/bcst-continue-card-icon.svg'
  import quizIllustration from '@/assets/images/bcst-quiz-illustration.svg'

  // Pure presentational band. The parent resolves the lesson/module/index/total from its
  // own data source (dashboard → store.continueLesson; module detail → nextInQueue) and
  // passes the scope flags, so this component has no store coupling and no variant branch.
  const props = defineProps({
    lesson: { type: Object, default: null },          // enriched lesson (null → complete fallback)
    module: { type: Object, default: null },          // module doc for "Category: …"
    index: { type: Number, default: null },           // X in "Lesson X of Y" / N in "Quiz N"
    total: { type: Number, default: null },           // Y in "Lesson X of Y"
    thumbnailUrl: { type: String, default: null },    // pre-resolved by the parent
    // True when no lesson is completed in the current scope (course-wide for the dashboard,
    // per-module for category) — drives the "Start Here" vs "Next Lesson in Queue" header.
    isFirstInScope: { type: Boolean, default: false },
    // Render the "all lessons complete" band when there is no lesson (category only; the
    // dashboard parent simply does not render the card in that case).
    showCompleteFallback: { type: Boolean, default: false },
  })

  const {
    isVideo,
    isQuiz,
    isInProgress,
    isPassedOrFailed,
    questionLabel,
    plainDescription,
    isRestarting,
    goToLesson,
    goToRequiredLesson,
  } = useTransformLessonCard(() => props.lesson, { moduleGetter: () => props.module })

  // ── Header state (by progress) ─────────────────────────────────────────────
  // in_progress         → "Continue where you left off" + flame
  // not_started + first  → "Start Here" + flag-fill
  // not_started + prior  → "Next Lesson in Queue" + calendar-check
  const headerState = computed(() => {
    if (isInProgress.value) return 'continue'
    return props.isFirstInScope ? 'start' : 'next'
  })

  const headerLabel = computed(() => {
    if (headerState.value === 'continue') return 'Continue where you left off'
    if (headerState.value === 'start') return 'Start Here'
    return 'Next Lesson in Queue'
  })

  // ── Title: "Quiz N: …" for quizzes, "Lesson X of Y: …" for videos ──────────
  const lessonTitle = computed(() => {
    const l = props.lesson
    if (!l) return ''
    if (isQuiz.value && props.index !== null) {
      return `Quiz ${props.index}: ${l.title}`
    }
    if (isVideo.value && props.index !== null) {
      return `Lesson ${props.index} of ${props.total}: ${l.title}`
    }
    return l.title
  })

  // ── Meta line: "Video Length: X • Status" / "Quiz Length: N Questions • Status" ──
  const metaLine = computed(() => {
    const l = props.lesson
    if (!l) return ''
    if (isVideo.value) {
      // In progress → show how much has been watched; else Watched / Not Started.
      let statusPart
      if (l.status === 'watched') statusPart = 'Watched'
      else if (l.status === 'in_progress') statusPart = `Watched: ${l.watchedLabel || '0m'}`
      else statusPart = 'Not Started'
      return `Video Length: ${l.durationLabel || '—'}  •  ${statusPart}`
    }
    if (isQuiz.value) {
      const statusMap = { locked: 'Locked', passed: 'Passed', failed: 'Not Passed', in_progress: 'In Progress' }
      const statusLabel = statusMap[l.status] ?? 'Not Started'
      return `Quiz Length: ${questionLabel.value}  •  ${statusLabel}`
    }
    return ''
  })

  // ── Thumbnail badge: duration (video) / question count (quiz) ──────────────
  const thumbBadge = computed(() => {
    if (isVideo.value) return props.lesson?.durationLabel ?? null
    if (isQuiz.value) return questionLabel.value
    return null
  })

  // ── CTA labels ─────────────────────────────────────────────────────────────
  const primaryCtaLabel = computed(() => {
    if (isVideo.value) return isInProgress.value ? 'Continue Video Lesson' : 'Go to Video Lesson'
    if (isQuiz.value) {
      if (isPassedOrFailed.value) return 'Retake Quiz'
      return isInProgress.value ? 'Continue Quiz' : 'Go to Quiz'
    }
    return 'Go to Lesson'
  })

  // Quiz cards get a secondary "Review Video Lesson" (outline) button.
  const showSecondaryCta = computed(() => isQuiz.value)
</script>

<template>
  <div v-if="lesson" class="tcc-section">
    <div class="d-flex tcc-row">

      <!-- Left: thumbnail when available, otherwise type-specific placeholder + corner badge -->
      <div class="tcc-thumbnail-wrapper flex-shrink-0">
        <!-- Real thumbnail from Storage (video or quiz) -->
        <template v-if="props.thumbnailUrl">
          <img
            :src="props.thumbnailUrl"
            :alt="lesson.title"
            class="tcc-thumbnail-img" />
        </template>

        <!-- Video: play-circle placeholder when no thumbnail exists -->
        <div v-else-if="isVideo" class="tcc-thumbnail-placeholder">
          <i class="bi bi-play-circle tcc-placeholder-icon"></i>
        </div>

        <!-- Quiz: default quiz illustration when no thumbnail exists -->
        <div v-else-if="isQuiz" class="tcc-quiz-illustration">
          <img :src="quizIllustration" alt="" class="tcc-illustration-img" />
        </div>

        <!-- Corner badge: duration (video) / "N Questions" (quiz) -->
        <span v-if="thumbBadge" class="tcc-duration-badge">{{ thumbBadge }}</span>
      </div>

      <!-- Right: content column -->
      <div class="tcc-content flex-grow-1">

        <!-- Header label -->
        <p class="text-14 text-success fw-bold tcc-label">
          <img
            v-if="headerState === 'continue'"
            :src="continueCardIcon"
            alt=""
            class="tcc-continue-icon" />
          <i
            v-else-if="headerState === 'start'"
            class="bi bi-flag-fill tcc-label-bi"></i>
          <i
            v-else
            class="bi bi-calendar2-check-fill tcc-label-bi"></i>
          {{ headerLabel }}
        </p>

        <!-- Module name -->
        <p v-if="module" class="text-14 fw-bold tcc-meta">
          Category: {{ module.title }}
        </p>

        <!-- Lesson title -->
        <p class="tcc-title">
          <strong>{{ lessonTitle }}</strong>
        </p>

        <!-- Meta: "Video Length / Quiz Length • Status" -->
        <p v-if="metaLine" class="text-16 fw-bold tcc-video-meta">
          {{ metaLine }}
        </p>

        <!-- Description (bodyHtml for video, descriptionHtml for quiz) -->
        <p v-if="plainDescription" class="tcc-description">
          {{ plainDescription }}
        </p>

        <!-- CTA buttons: navigate to the lesson/quiz detail view. -->
        <div class="tcc-cta-row">
          <button
            class="btn btn-primary tcc-cta"
            :disabled="isRestarting"
            @click="goToLesson">
            <template v-if="isRestarting">
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ primaryCtaLabel }}
            </template>
            <template v-else>
              {{ primaryCtaLabel }}
              <i class="bi bi-chevron-right ms-2"></i>
            </template>
          </button>
          <button
            v-if="showSecondaryCta"
            class="btn btn-outline-primary tcc-cta tcc-cta--secondary"
            @click="goToRequiredLesson">
            Review Video Lesson
            <i class="bi bi-chevron-right ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Category fallback: all lessons in the module are complete -->
  <div v-else-if="showCompleteFallback" class="tcc-section tcc-section--complete">
    <div class="tcc-complete-content">
      <i class="bi bi-check-circle-fill tcc-success-icon"></i>
      <p class="tcc-complete-msg">Congratulations! You've completed all lessons in this category!</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* Brand accents without a Bootstrap token equivalent (Figma "Eggplant - Accent Light"
     and the quiz media background). */
  $tld-accent-light: #f7f1fd;
  $tld-quiz-media-bg: #c4c4c4;

  /* Full-bleed lavender band — Figma nodes 23493:5876 / 23505:55089 (h=360px).
   Internal padding mirrors bcs-page-padding (9% left / 8.5% right) so the
   thumbnail and text align with the rest of the page content. */
  .tcc-section {
    background-color: $tld-accent-light;
    min-height: 360px;             /* Figma band height */
    display: flex;
    flex-direction: column;
    justify-content: center;       /* center the row within the 360px band */
    padding: 24px 8.5% 24px 9%;    /* min vertical gutter; grows only if content exceeds 360px */
  }

  /* Completion state: minimal band (opt out of the fixed 360px height) */
  .tcc-section--complete {
    min-height: 0;
    display: block;
    padding: 28px 9%;
  }

  .tcc-complete-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .tcc-success-icon {
    font-size: 24px;
    color: var(--bs-success);
    flex-shrink: 0;
  }

  .tcc-complete-msg {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: var(--bs-success);
    margin: 0;
    flex-shrink: 0;
  }

  .tcc-row {
    align-items: flex-start;
    gap: 43px;
  }

  /* ── Thumbnail ─────────────────────────────────────────────────────────── */

  /* Figma: 430×260px, border-radius 4.8px */
  .tcc-thumbnail-wrapper {
    position: relative;
    width: 430px;
    min-height: 260px;      /* Figma minimum when content is short */
    align-self: stretch;    /* fill the row height so its bottom aligns with the buttons */
    border-radius: 4.8px;
    overflow: hidden;
    background-color: var(--bs-gray-200);
    flex-shrink: 0;
  }

  .tcc-thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .tcc-thumbnail-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .tcc-placeholder-icon {
    font-size: 3rem;
    color: var(--bs-gray-500);
  }

  /* Quiz illustration: centered on the gray media area */
  .tcc-quiz-illustration {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-color: $tld-quiz-media-bg;
  }

  .tcc-illustration-img {
    max-width: 85%;
    max-height: 85%;
    object-fit: contain;
  }

  /* Corner badge: flush top-left, gray, only TL+BR rounded */
  .tcc-duration-badge {
    position: absolute;
    top: 0;
    left: 0;
    background-color: var(--bs-gray-600);
    color: var(--bs-white);
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.28px;
    padding: 4px 8px;
    border-radius: 4.8px 0 4px 0; /* TL BR only — matches Figma rounded-tl + rounded-br */
  }

  /* ── Content column ─────────────────────────────────────────────────────── */

  /* Flame icon (continue) — 20×20px SVG, vertically centered against the text line */
  .tcc-continue-icon {
    width: 20px;
    height: 20px;
    vertical-align: middle;
    margin-right: 12px;
  }

  /* Bootstrap header icons (flag / calendar-check) — green, matches text-success */
  .tcc-label-bi {
    font-size: 1rem;
    vertical-align: middle;
    margin-right: 10px;
  }

  .tcc-label {
    letter-spacing: -0.28px;
    margin-bottom: 8px;
  }

  /* Category/Module text: project primary (purple) */
  .tcc-meta {
    color: var(--bs-primary);
    letter-spacing: -0.28px;
    margin-bottom: 4px;
  }

  /* Title: 22px Bold Purple — Figma header-22 */
  .tcc-title {
    font-size: 1.375rem;
    line-height: 1.25;
    letter-spacing: -0.44px;
    color: var(--bs-primary);
    margin-top: 4px;
    margin-bottom: 12px;
  }

  /* Meta ("Video/Quiz Length • Status"): 16px Bold Body Color */
  .tcc-video-meta {
    color: var(--bs-body-color);
    letter-spacing: -0.32px;
    margin-top: 12px;
    margin-bottom: 11px;
  }

  /* Description: 16px Regular Gray — Figma text-16, max-width 700px, 3-line clamp */
  .tcc-description {
    font-size: 1rem;
    line-height: 1.3;
    letter-spacing: -0.32px;
    color: var(--bs-gray-700);
    max-width: 700px;
    margin-bottom: 16px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    overflow: hidden;
  }

  /* CTA buttons — 20px font, ~260px primary, side by side with gap */
  .tcc-cta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .tcc-cta {
    font-size: 1.25rem;
    line-height: 1.5;
    padding: 9px 17px;
    border-radius: 4.8px;
    min-width: 188px;
  }

  .tcc-cta--secondary {
    min-width: 275px;
  }
</style>
